import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, AlertCircle, Clock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import './AnalysisRunner.css';

const AnalysisRunner = () => {
  const [selectedPipeline, setSelectedPipeline] = useState('single_prompt');
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // idle, running, success, error
  const [analysisResults, setAnalysisResults] = useState(null);
  const [availablePipelines, setAvailablePipelines] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState({});

  // Load pipeline data on component mount
  useEffect(() => {
    const loadPipelineData = async () => {
      try {
        const [pipelinesResponse, statusResponse] = await Promise.all([
          apiService.getAvailablePipelines(),
          apiService.getPipelineStatus()
        ]);
        setAvailablePipelines(pipelinesResponse.data.pipelines || []);
        setPipelineStatus(statusResponse.data || {});
      } catch (error) {
        console.error('Failed to load pipeline data:', error);
        toast.error('Failed to load pipeline information');
      }
    };
    loadPipelineData();
  }, []);

  const runAnalysis = async () => {
    try {
      setAnalysisStatus('running');
      setAnalysisResults(null);
      
      const response = await apiService.runAnalysis(selectedPipeline);
      
      setAnalysisResults(response.data);
      setAnalysisStatus('success');
      toast.success(`Analysis completed successfully using ${selectedPipeline} pipeline!`);
      
      // Refresh pipeline status after analysis
      const statusResponse = await apiService.getPipelineStatus();
      setPipelineStatus(statusResponse.data || {});
      
    } catch (error) {
      setAnalysisStatus('error');
      toast.error('Failed to run analysis');
      console.error('Analysis error:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'not_ready':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'not_ready':
        return 'Not Ready';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'text-green-600 bg-green-100';
      case 'not_ready':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-slate-400 bg-slate-700';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-100 mb-2">Analysis Runner</h1>
          <p className="text-slate-400">
            Run analysis on your proposals using different pipelines to extract patterns and insights.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pipeline Selection */}
          <div className="lg:col-span-1">
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Select Pipeline</h2>
              
              <div className="space-y-4">
                {availablePipelines.map((pipeline) => (
                  <div
                    key={pipeline.id}
                    className={`relative cursor-pointer border-2 rounded-lg p-4 transition-all ${
                      selectedPipeline === pipeline.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPipeline(pipeline.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                        selectedPipeline === pipeline.id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedPipeline === pipeline.id && (
                          <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-100">{pipeline.name}</h4>
                        <p className="text-sm text-slate-400 mt-1">{pipeline.description}</p>
                        <div className="mt-2 text-xs text-slate-400">
                          <span className="font-medium">Stages:</span> {pipeline.stages}
                        </div>
                        
                        {/* Pipeline Status */}
                        {pipelineStatus[pipeline.id] && (
                          <div className={`mt-2 text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${
                            getStatusColor(pipelineStatus[pipeline.id].ready ? 'ready' : 'not_ready')
                          }`}>
                            {getStatusIcon(pipelineStatus[pipeline.id].ready ? 'ready' : 'not_ready')}
                            {getStatusText(pipelineStatus[pipeline.id].ready ? 'ready' : 'not_ready')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Run Analysis Button */}
              <div className="mt-6">
                <button
                  onClick={runAnalysis}
                  disabled={analysisStatus === 'running'}
                  className="w-full btn btn-primary"
                >
                  <Play className="w-4 h-4" />
                  {analysisStatus === 'running' ? 'Running Analysis...' : 'Run Analysis'}
                </button>
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="lg:col-span-2">
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Analysis Results</h2>
              
              {analysisStatus === 'idle' && (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a pipeline and click "Run Analysis" to start</p>
                </div>
              )}

              {analysisStatus === 'running' && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-slate-400">Running analysis...</p>
                </div>
              )}

              {analysisStatus === 'error' && (
                <div className="text-center py-12 text-red-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                  <p>Analysis failed. Please try again.</p>
                </div>
              )}

              {analysisStatus === 'success' && analysisResults && (
                <div className="space-y-6">
                  {/* Analysis Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <h3 className="font-medium text-green-900">Analysis Completed Successfully</h3>
                    </div>
                    <p className="text-green-700 text-sm">
                      Pipeline: {analysisResults.pipeline_type} | 
                      Timestamp: {new Date(analysisResults.timestamp).toLocaleString()}
                    </p>
                  </div>

                  {/* Results Details */}
                  {analysisResults.pipeline_type === 'multi_stage' && (
                    <div>
                      <h4 className="font-medium text-slate-100 mb-3">Output Files</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(analysisResults.output_files || {}).map(([key, file]) => (
                          <div key={key} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-100">{key}</p>
                              <p className="text-xs text-slate-400">{file.path}</p>
                            </div>
                            {file.exists && (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysisResults.pipeline_type === 'single_prompt' && (
                    <div>
                      <h4 className="font-medium text-slate-100 mb-3">Analysis Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">
                            {analysisResults.analysis_results?.total_proposals || 0}
                          </p>
                          <p className="text-xs text-blue-700">Total Proposals</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">
                            {analysisResults.analysis_results?.successful_analyses || 0}
                          </p>
                          <p className="text-xs text-green-700">Successful</p>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <p className="text-2xl font-bold text-yellow-600">
                            {analysisResults.analysis_results?.failed_analyses || 0}
                          </p>
                          <p className="text-xs text-yellow-700">Failed</p>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <p className="text-2xl font-bold text-purple-600">
                            {analysisResults.analysis_results?.combined_insights?.common_patterns?.length || 0}
                          </p>
                          <p className="text-xs text-purple-700">Patterns Found</p>
                        </div>
                      </div>

                      {/* Combined Insights */}
                      {analysisResults.analysis_results?.combined_insights && (
                        <div className="mt-6">
                          <h4 className="font-medium text-slate-100 mb-3">Combined Insights</h4>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium text-slate-300">Common Patterns:</p>
                              <p className="text-sm text-slate-400">
                                {analysisResults.analysis_results.combined_insights.common_patterns?.join(', ') || 'None found'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-300">Style Insights:</p>
                              <p className="text-sm text-slate-400">
                                {analysisResults.analysis_results.combined_insights.style_insights?.join(', ') || 'None found'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-300">Activity Types:</p>
                              <p className="text-sm text-slate-400">
                                {analysisResults.analysis_results.combined_insights.activity_types?.join(', ') || 'None found'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisRunner; 